import { Switch, useLocation, useRouteMatch } from 'react-router-dom';
import { Route } from '@modernhealth/web-analytics';
import React, { useLayoutEffect } from 'react';

import { CirclesErrorComponent } from './pages/CirclesErrorComponent';
import { CirclesPage } from './pages/CirclesPage';
import { CirclesSeriesPage } from './pages/CirclesSeriesPage';
import { CirclesTopicPage } from './pages/CirclesTopicPage';
import { CirclesYourCirclesPage } from './pages/CirclesYourCirclesPage';
import UpcomingCirclesPage from './pages/UpcomingCirclesPage';
import { CirclesRsvpAddToCalendarPage } from './pages/CirclesRsvpAddToCalendarPage';
import { CirclesRsvpConfirmationPage } from './pages/CirclesRsvpConfirmationPage';
import { CirclesCancellationPage } from './pages/CirclesCancellationPage';
import { CirclesRsvpPage } from './pages/CirclesRsvpPage';
import { CirclesAboutPage } from './pages/CirclesAboutPage';
import { CirclesCommunityGuidelinesPage } from './pages/CirclesCommunityGuidelinesPage';
import { CirclesOnDemandOverviewPage } from './pages/CirclesOnDemandOverviewPage';
import { CirclesOnDemandCirclePage } from './pages/CirclesOnDemandCirclePage';
import { CirclesOnDemandLanguagePage } from './pages/CirclesOnDemandLanguagePage';

export const routes = {
    topic<TopicSlug extends string>(topicSlug: TopicSlug) {
        let topic = `/circles/${topicSlug}` as const;

        return {
            path: topic,
            registered<RsvpId extends string>(rsvpId: RsvpId) {
                const registered = `${topic}/registered/${rsvpId}` as const;

                return {
                    path: registered,
                    cancel: `${registered}/cancel` as const,
                    confirmation: `${registered}/confirmation` as const,
                    addToCalendar: `${registered}/add-to-calendar` as const,
                };
            },
            session<SessionId extends string>(sessionId: SessionId) {
                const session = `${topic}/session/${sessionId}` as const;

                return {
                    path: session,
                    register: `${session}/register` as const,
                };
            },
        };
    },
};

const ScrollToTop = () => {
    const { pathname } = useLocation();

    useLayoutEffect(() => {
        window.scrollTo(0, 0);
    }, [pathname]);

    return null;
};

export const CirclesRouter = () => {
    const { path } = useRouteMatch();

    return (
        <>
            <ScrollToTop />
            <Switch>
                <Route
                    path={`${path}/`}
                    analytics={{ pageName: 'circles' }}
                    component={CirclesPage}
                    exact
                />
                <Route
                    path={`${path}/upcoming`}
                    analytics={{ pageName: 'circles-upcoming' }}
                    component={UpcomingCirclesPage}
                />
                <Route
                    path={`${path}/about`}
                    analytics={{ pageName: 'circles-about' }}
                    component={CirclesAboutPage}
                    exact
                />
                <Route
                    path={`${path}/guidelines`}
                    analytics={{ pageName: 'circles-community-guidelines' }}
                    component={CirclesCommunityGuidelinesPage}
                    exact
                />
                <Route
                    path={`${path}/registered`}
                    analytics={{ pageName: 'circles-registered' }}
                    component={CirclesYourCirclesPage}
                    exact
                />
                <Route
                    path={`${path}/series/:seriesSlug`}
                    analytics={{ pageName: 'circles-series' }}
                    component={CirclesSeriesPage}
                />
                <Route
                    path="circles/error"
                    analytics={{ pageName: 'circles-error' }}
                    component={CirclesErrorComponent}
                    exact
                />
                <Route
                    path={`${path}/on-demand`}
                    analytics={{ pageName: 'circles-on-demand-overview' }}
                    component={CirclesOnDemandOverviewPage}
                    exact
                />
                <Route
                    path={`${path}/on-demand/:topicSlug`}
                    analytics={{ pageName: 'circles-on-demand' }}
                    component={CirclesOnDemandCirclePage}
                    exact
                />
                <Route
                    path={`${path}/language/:languageCode`}
                    analytics={{ pageName: 'circles-by-language' }}
                    component={CirclesOnDemandLanguagePage}
                    exact
                />
                <Route
                    path={`${path}/:topicSlug/registered/:rsvpId/confirmation`}
                    analytics={{ pageName: 'circles-confirmation' }}
                    component={CirclesRsvpConfirmationPage}
                    exact
                />
                <Route
                    path={`${path}/:topicSlug/registered/:rsvpId/add-to-calendar`}
                    analytics={{ pageName: 'circles-add-to-calendar' }}
                    component={CirclesRsvpAddToCalendarPage}
                    exact
                />
                <Route
                    path={`${path}/:topicSlug`}
                    analytics={{ pageName: 'circles-topic' }}
                    component={CirclesTopicPage}
                    exact
                />
                <Route
                    path={`${path}/:topicSlug/registered/:rsvpId/cancel`}
                    analytics={{ pageName: 'circles-cancellation' }}
                    component={CirclesCancellationPage}
                    exact
                />
                <Route
                    path={`${path}/:topicSlug/session/:sessionId/register`}
                    analytics={{ pageName: 'circles-registration-form' }}
                    component={CirclesRsvpPage}
                    exact
                />
            </Switch>
        </>
    );
};
